import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ChatNotificacion, ChatNotificationsService } from '../../services/chat-notifications.service';

const PALETTE = [
  '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B',
  '#10B981', '#06B6D4', '#EF4444', '#6366F1',
  '#14B8A6', '#F97316',
];
function colorPara(id: number): string { return PALETTE[id % PALETTE.length]; }
function iniciales(nombre: string): string {
  return nombre.split(' ').filter(Boolean).slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '').join('');
}

interface ToastState extends ChatNotificacion {
  /** Timestamp de creacion — para ordenar y para la barra de progreso. */
  shownAt: number;
  /** Si el user esta hovering, pausamos el auto-dismiss. */
  paused: boolean;
  /** UI helpers derivados de remitenteId / remitenteNombre. */
  avatarColor: string;
  avatarIniciales: string;
}

const TOAST_TTL_MS = 6000;
const TOAST_MAX_STACK = 3;

/**
 * Stack de toasts in-app para notificaciones de chat. Se monta una unica
 * instancia en `app-root`. Estilo Teams/Slack: slide-in desde la esquina
 * inferior derecha, avatar circular, nombre, preview de 2 lineas, barra de
 * progreso de auto-dismiss, click → /chat con la conversacion.
 *
 * Stack-up animation (toasts mas viejos suben), max 3 visibles. Al hover
 * pausa el dismiss. Click en X cierra. Hover sobre uno pausa todos.
 */
@Component({
  selector: 'app-chat-notification-overlay',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './chat-notification-overlay.component.html',
  styleUrls: ['./chat-notification-overlay.component.scss'],
})
export class ChatNotificationOverlayComponent implements OnInit {
  private notifService = inject(ChatNotificationsService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  toasts = signal<ToastState[]>([]);
  /** Cuando hay hover sobre cualquier toast — pausa todos los timers. */
  private hovering = false;

  /**
   * "Reloj" snapshotteado que el template usa para `progress(t)` y
   * `formatRelative(...)`. Sin esto, llamabamos `Date.now()` directo desde
   * el template y entre las dos pasadas de change detection que Angular hace
   * en dev mode el valor cambiaba → NG0100 ExpressionChangedAfterItHasBeenChecked.
   * Solucion: el signal solo cambia en el tick del setInterval, asi durante un
   * mismo ciclo CD `now()` es estable.
   */
  private now = signal(Date.now());

  ngOnInit() {
    this.notifService.notifications$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(n => this.push(n));

    // Tick global cada 100ms para evaluar dismiss + refrescar el reloj
    // (un solo loop, todos los tiempos sincronizados). 100ms es suficiente
    // para que la barra de progreso se vea fluida sin spamear CD.
    const tick = setInterval(() => {
      this.now.set(Date.now());
      this.evaluateDismiss();
    }, 100);
    this.destroyRef.onDestroy(() => clearInterval(tick));
  }

  private push(notif: ChatNotificacion) {
    this.toasts.update(arr => {
      // Si ya hay un toast de la misma conversacion, lo reemplazamos para
      // que no se acumulen multiples preview de la misma charla.
      const filtered = arr.filter(t => t.conversacionId !== notif.conversacionId);
      const next: ToastState = {
        ...notif,
        shownAt: Date.now(),
        paused: false,
        avatarColor: colorPara(notif.remitenteId),
        avatarIniciales: iniciales(notif.remitenteNombre),
      };
      const stacked = [...filtered, next];
      // Mantenemos los N mas recientes.
      return stacked.slice(-TOAST_MAX_STACK);
    });
  }

  private evaluateDismiss() {
    if (this.hovering) return;
    const now = this.now();
    const survivors = this.toasts().filter(t => now - t.shownAt < TOAST_TTL_MS);
    if (survivors.length !== this.toasts().length) {
      this.toasts.set(survivors);
    }
  }

  // ===== UI handlers =====

  onMouseEnter(_id: number) {
    this.hovering = true;
    // Reseteamos shownAt para que al despausar tenga "vida" otra vez.
    this.toasts.update(arr => arr.map(t => ({ ...t, paused: true })));
  }

  onMouseLeave(_id: number) {
    this.hovering = false;
    const now = Date.now();
    this.toasts.update(arr => arr.map(t => ({ ...t, paused: false, shownAt: now })));
  }

  open(notif: ToastState) {
    // El query param `u` apunta al "otro usuario" — el chat.component lo usa
    // para abrir la conversación directamente. Coincide con el deep-link que
    // emite la Browser Notification del SO.
    this.router.navigate(['/chat'], { queryParams: { u: notif.remitenteId } });
    this.dismiss(notif.id);
  }

  dismiss(id: number) {
    this.toasts.update(arr => arr.filter(t => t.id !== id));
  }

  /** Calcula el % de tiempo consumido para la barra de progreso. */
  progress(t: ToastState): number {
    if (t.paused) return 0;
    const elapsed = this.now() - t.shownAt;
    return Math.max(0, Math.min(100, (elapsed / TOAST_TTL_MS) * 100));
  }

  /** Tiempo relativo "hace X" — el toast lo muestra para contexto. */
  formatRelative(date: Date): string {
    const diff = this.now() - new Date(date).getTime();
    const sec = Math.floor(diff / 1000);
    if (sec < 5) return 'recién';
    if (sec < 60) return `hace ${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `hace ${min} min`;
    return new Date(date).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  }

  trackById(_i: number, t: ToastState) { return t.id; }
}
